<template>
  <el-card v-loading="isLoading" shadow="always">
    <el-table :data="tableRows" style="width: 100%;" stripe border :cell-style="cellStyle">
      <el-table-column align="center" :label="$t('dashboard.statusTable.colName')">
        <template slot-scope="scope">
          <span class="dot" :style="{ backgroundColor: getStatusColor(scope.row.status), marginRight: '6px' }"></span>
          <span>{{ scope.row.name }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="status" align="center" :label="$t('dashboard.statusTable.colStatus')">
        <template slot-scope="scope">
          {{ $t(statusTextKey(scope.row.status)) }}
        </template>
      </el-table-column>
      <el-table-column align="center" :label="$t('dashboard.statusTable.colValue')">
        <template slot-scope="scope">
          <span v-if="scope.row.reading2 !== null">{{ formatReading2(scope.row.reading2) }} {{ scope.row.unit }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" :label="$t('dashboard.statusTable.colAL')">
        <template slot-scope="scope">
          {{ formatThreshold(scope.row.AL, 'AL_plus', 'AL_minus', scope.row.unit) }}
        </template>
      </el-table-column>
      <el-table-column align="center" :label="$t('dashboard.statusTable.colWSL')">
        <template slot-scope="scope">
          {{ formatThreshold(scope.row.WSL, 'WSL_plus', 'WSL_minus', scope.row.unit) }}
        </template>
      </el-table-column>
    </el-table>
  </el-card>
</template>

<script>
import { getStatusColor } from '@/lib/base/systemAlert.js';

// 狀態嚴重度排序（本元件專用，數字越大越嚴重）：WSL > AL > WL > down > GREEN
// 不與 DataList.vue 的 priority 共用，因為那個排序會因 kmrt 角色被翻轉 down 的優先度。
const STATUS_SEVERITY = { GREEN: 0, down: 1, WL: 2, AL: 3, WSL: 4 };

const STATUS_TEXT_KEY = {
  WSL: 'dashboard.statusTable.statusWSL',
  AL: 'dashboard.statusTable.statusAL',
  WL: 'dashboard.statusTable.statusWL',
  GREEN: 'dashboard.statusTable.statusGreen',
  down: 'dashboard.statusTable.statusDown',
};

export default {
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    instrumentGroupList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      allInstrumentData: [],
    };
  },
  computed: {
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    tableRows() {
      return (this.rows || []).map(rowConfig => this.resolveRow(rowConfig));
    },
  },
  async mounted() {
    await this.Init();
  },
  methods: {
    async Init() {
      this.isLoading = true;
      try {
        const res = await this.$axios({
          category: 'project',
          method: 'get',
          apiName: `${this.selectedProject._id}/data/last/${Date.now()}`,
        });
        this.allInstrumentData = res.data;
      } catch (error) {
        this.$notifyError(error);
      } finally {
        this.isLoading = false;
      }
    },
    findInstrumentById(id) {
      return this.allInstrumentData.find(instrument => instrument._id === id) || null;
    },
    // 同組儀器理論上警戒值/行動值相同，此處僅在無法保證一致時，
    // 以狀態最嚴重的成員代表整組顯示（儀器組本身沒有警戒值/行動值欄位）。
    resolveGroupTarget(groupId) {
      const group = this.instrumentGroupList.find(g => g._id === groupId);
      if (!group) return null;
      const members = (group.instrumentList || [])
        .map(entry => this.findInstrumentById(entry.instrument))
        .filter(Boolean);
      if (members.length === 0) return null;
      return members.reduce((worst, current) => {
        const worstSeverity = STATUS_SEVERITY[worst.status] ?? -1;
        const currentSeverity = STATUS_SEVERITY[current.status] ?? -1;
        return currentSeverity > worstSeverity ? current : worst;
      });
    },
    resolveRow(rowConfig) {
      const target = rowConfig.kind === 'group'
        ? this.resolveGroupTarget(rowConfig.id)
        : this.findInstrumentById(rowConfig.id);
      if (!target) {
        return { name: rowConfig.name, status: 'down', reading2: null, unit: '', AL: {}, WSL: {} };
      }
      return {
        name: rowConfig.name,
        status: target.status,
        reading2: target.reading2,
        unit: target.unit,
        AL: target.AL || {},
        WSL: target.WSL || {},
      };
    },
    statusTextKey(status) {
      return STATUS_TEXT_KEY[status] || STATUS_TEXT_KEY.down;
    },
    getStatusColor(status) {
      return getStatusColor(status);
    },
    formatReading2(value) {
      return Number(value).toFixed(1);
    },
    cellStyle({ row, column }) {
      if (column.property === 'status') {
        return { backgroundColor: this.getStatusColor(row.status), color: '#000' };
      }
      return {};
    },
    formatThreshold(thresholdObj, plusKey, minusKey, unit) {
      if (!thresholdObj) return '';
      const empty = [null, undefined];
      const plus = thresholdObj[plusKey];
      const minus = thresholdObj[minusKey];
      if (!empty.includes(plus) && !empty.includes(minus)) {
        if (Math.abs(plus) === Math.abs(minus)) {
          return `± ${plus} ${unit}`;
        }
        return `${plus} ${unit} ~ ${minus} ${unit}`;
      } else if (!empty.includes(plus)) {
        return `${plus} ${unit}`;
      } else if (!empty.includes(minus)) {
        return `${minus} ${unit}`;
      }
      return '';
    },
  },
};
</script>

<style scoped>
::v-deep .el-table {
  border: 2px solid #000;
}

::v-deep .el-table--border::after,
::v-deep .el-table--group::after {
  width: 2px;
  background-color: #000;
}

::v-deep .el-table--border::before {
  height: 2px;
  background-color: #000;
}

::v-deep .el-table__header th.el-table__cell {
  font-weight: bold;
  font-size: 20px;
  border-bottom: 2px solid #000 !important;
  border-right: 1px solid #000 !important;
}

::v-deep .el-table__body td.el-table__cell {
  font-size: 20px;
  border-bottom: 1px solid #000 !important;
  border-right: 1px solid #000 !important;
}

::v-deep .el-table__body td.el-table__cell:nth-child(1),
::v-deep .el-table__body td.el-table__cell:nth-child(2),
::v-deep .el-table__body td.el-table__cell:nth-child(3) {
  font-weight: bold;
}
</style>
